'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useMemo, useState, useCallback } from 'react';
import Image from 'next/image';
import { HeadRegex, BMIRegex } from '@/utils/lm_constant_utils';
import { regeneratePath } from '@/utils/lm_utilities';

// Dynamic imports with proper handling of default exports
const CloseIcon = dynamic(() => import('@mui/icons-material/Close'), { ssr: false });
import { ModalBody, ModalFooter, Modal } from '@/components/reactstrap';

const Sidebar = () => {
  const pathname = usePathname();
  const [show, setShow] = useState(false);

  const showMobilemenu = useCallback(() => {
    document.getElementById('sidebarArea').classList.toggle('showSidebar');
  }, []);

  const hideMobilemenu = useCallback(() => {
    document.getElementById('sidebarArea').classList.toggle('showSidebar');
  }, []);

  const handleClose = useCallback(() => setShow(false), []);
  // const handleShow = useCallback((e) => {
  //   e.preventDefault();
  //   setShow(true);
  // }, []);

  const isAgeValidToShow = useMemo(() => {
    return (regex) => {
      const age = pathname?.split('/')[3];
      return age ? regex.test(age) : true;
    };
  }, [pathname]);

  return (
    <div>
      <Modal centered isOpen={show} toggle={handleClose}>
        <ModalBody>DISCUSSIONS COMING SOON!!!</ModalBody>
        <ModalFooter>
          <button variant="primary" onClick={handleClose} color="primary">
            AWESOME!
          </button>
        </ModalFooter>
      </Modal>

      <div className="d-flex align-items-center"></div>
      <div>
        <div className="p-2 d-flex">
          <button id="close" aria-label="close" color="white" className="ms-auto d-lg-none" onClick={showMobilemenu}>
            <CloseIcon fontSize="large" sx={{ fill: '#fff' }} />
          </button>
        </div>
      </div>

      <div className="mt-2">
        <nav className="nav flex-column sidebarNav">
          <div className="nav-item sideBarText py-3" style={{ padding: '0.5rem 1rem' }}>
            <Image
              priority
              src="/assets/images/sidebarIcons/chart.svg"
              alt="lifemeasure charts"
              height="24"
              width="24"
            />
            <span className="ms-2 d-inline-block">Charts</span>
          </div>
          <div>
            <nav className="nav flex-column">
              <div className="nav-item sidenav-bg">
                <Link
                  prefetch={false}
                  href={{ pathname: regeneratePath(pathname, 'height-charts') }}
                  className={`nav-link sideBarsubText py-3 ${pathname.includes('height-charts') ? 'active' : ''}`}
                  onClick={hideMobilemenu}
                >
                  <Image
                    priority
                    src="/assets/images/sidebarIcons/height.svg"
                    alt="height chart at lifemeasure"
                    height="24"
                    width="24"
                  />
                  <span className="ms-2 d-inline-block">Height</span>
                </Link>
              </div>
              <div className="nav-item sidenav-bg">
                <Link
                  prefetch={false}
                  href={{ pathname: regeneratePath(pathname, 'weight-charts') }}
                  className={`nav-link sideBarsubText py-3 ${pathname.includes('weight-charts') ? 'active' : ''}`}
                  onClick={hideMobilemenu}
                >
                  <Image
                    priority
                    src="/assets/images/sidebarIcons/weight.svg"
                    alt="weight chart at lifemeasure"
                    height="24"
                    width="24"
                  />
                  <span className="ms-2 d-inline-block">Weight</span>
                </Link>
              </div>
              {isAgeValidToShow(HeadRegex) && (
                <HeadNavItem pathname={pathname} currentPath={pathname} hideMobilemenu={hideMobilemenu} />
              )}
              {isAgeValidToShow(BMIRegex) && (
                <BmiNavItem pathname={pathname} currentPath={pathname} hideMobilemenu={hideMobilemenu} />
              )}
            </nav>
          </div>
          <div className="nav-item sidenav-bg">
            <Link
              prefetch={false}
              href="/calculators"
              className={`nav-link sideBarText py-3 ${pathname === '/calculators' ? 'active' : ''}`}
              onClick={hideMobilemenu}
            >
              <Image priority src="/assets/images/sidebarIcons/calc.svg" alt="calcIcon" height={24} width={24} />
              <span className="ms-2 d-inline-block">Calculators</span>
            </Link>
          </div>
          <div className="nav-item sidenav-bg">
            <Link prefetch={false} href="/info" className="nav-link sideBarText py-3">
              <Image priority src="/assets/images/sidebarIcons/message.svg" alt="message" height="24" width="24" />
              <span className="ms-2 d-inline-block">Articles</span>
            </Link>
          </div>
          <div className="nav-item footer-sidebar text-center">
            <div className="col">
              <div className="text-center pb-2 pb-lg-3">
                <Link prefetch={false} href="/" className="nav-link" onClick={hideMobilemenu}>
                  <Image
                    priority
                    className="w-25"
                    src="/assets/images/logos/LM-round.svg"
                    alt="logo"
                    height="62"
                    width="62"
                  />
                </Link>
                <Image
                  priority
                  className="mt-2 mt-lg-2 lines"
                  src="/assets/images/lines.svg"
                  alt="lines"
                  height="20"
                  width="100"
                />
              </div>
            </div>
            <div className="d-flex">
              <div className="col">
                <Link prefetch={false} href="/about" className="footerText top-nav-link" onClick={hideMobilemenu}>
                  <span>About</span>
                </Link>
              </div>
              <div className="col">
                <Link prefetch={false} href="/faqs" className="footerText top-nav-link" onClick={hideMobilemenu}>
                  <span>FAQs</span>
                </Link>
              </div>
              <div className="col">
                <Link prefetch={false} href="/contact" className="footerText top-nav-link" onClick={hideMobilemenu}>
                  <span>Contact</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="nav-item footer-sidebar-signup text-end">
            <Link prefetch={false} href="/signup" className="signup-btn">
              Sign Up
            </Link>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;

const HeadNavItem = ({ hideMobilemenu, pathname, currentPath }) => (
  <div className="nav-item sidenav-bg">
    <Link
      prefetch={false}
      href={{ pathname: regeneratePath(currentPath, 'head-charts') }}
      className={`nav-link sideBarsubText py-2 ${pathname.includes('head-charts') ? 'active' : ''}`}
      onClick={hideMobilemenu}
    >
      <Image
        priority
        src="/assets/images/sidebarIcons/head.svg"
        alt="head chart at lifemeasure"
        height="24"
        width="24"
      />
      <span className="ms-2 d-inline-block">Head</span>
    </Link>
  </div>
);

const BmiNavItem = ({ hideMobilemenu, pathname, currentPath }) => (
  <div className="nav-item sidenav-bg">
    <Link
      prefetch={false}
      href={{ pathname: regeneratePath(currentPath, 'bmi-charts') }}
      className={`nav-link sideBarsubText py-2 ${pathname.includes('bmi-charts') ? 'active' : ''}`}
      onClick={hideMobilemenu}
    >
      <Image priority src="/assets/images/sidebarIcons/bmi.svg" alt="bmi chart at lifemeasure" height="24" width="24" />
      <span className="ms-2 d-inline-block">BMI</span>
    </Link>
  </div>
);
